import { apiToken } from '../auth';
import { runtimeConfig } from '../runtime';
import { harvest, logCall } from '../lib/devlog';

/**
 * THE ONLY MODULE IN THE SPA THAT CONSTRUCTS AN AUTH HEADER.
 *
 * It sends the ACCESS token, which is the exact opposite of what this file
 * said under Cognito. azure/functions/auth.py decodes with
 * `audience=FAMILY_AUDIENCE`, and auth_setup.sh sets that to the family app's
 * CLIENT ID — which is the `aud` of an access token minted for that API. An id
 * token's `aud` is the client that asked for it, so sending one produces a 401
 * indistinguishable from a bad password. One claim apart, an afternoon deep.
 *
 * Every other module calls api/endpoints.ts. If you find yourself importing
 * `apiToken` anywhere else, the invariant is already broken.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await apiToken();
  // A null token means there is no usable session — nobody signed in, or a
  // session that has just been discovered to be dead. It is deliberately ONE
  // case here: auth.ts has already dropped the dead session and told the app to
  // go back to the sign-in screen, so there is nothing this layer would do
  // differently for the two. (It used to claim null meant "no account at all",
  // which was true of only one of them and sent debuggers looking for a missing
  // account when the real story was a 24-hour refresh token expiring.)
  //
  // Thrown before any fetch, so the dev panel logs it as the auth failure it is
  // rather than as a request that was never made.
  if (!token) throw new ApiError(401, 'not signed in', null);
  return { authorization: `Bearer ${token}` };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  /** The three open routes (/catalog/starters, pairing claim, webhooks). */
  anonymous?: boolean;
  /** Shares one namespace with the client-side memo — that is the point. */
  idempotencyKey?: string;
  query?: Record<string, string | undefined>;
  /** Purely a dev-panel label; does not change the request. */
  speculative?: boolean;
  note?: string;
};

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { apiEndpoint } = runtimeConfig();
  const method = options.method ?? 'GET';

  const url = new URL(apiEndpoint + path);
  for (const [k, v] of Object.entries(options.query ?? {})) {
    if (v !== undefined) url.searchParams.set(k, v);
  }

  const startedAt = performance.now();
  let status = 0;
  let payload: unknown = null;
  let error: string | undefined;

  try {
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;
    // Inside the try so an expired or absent session shows up in the dev
    // panel as a failed call. A silently-unlogged auth failure looks exactly
    // like "the request was never made", which is the wrong thing to debug.
    if (!options.anonymous) Object.assign(headers, await authHeaders());

    const res = await fetch(url.toString(), {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
    status = res.status;
    const text = await res.text();
    // auth.py's own refusals (401/403) are JSON, but the Functions host
    // answers a cold-start failure with HTML — parse defensively so a host
    // hiccup surfaces as the status it is rather than as a SyntaxError from
    // deep inside a screen.
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      payload = { raw: text.slice(0, 400) };
    }
    if (!res.ok) {
      error = describe(payload) || `HTTP ${status}`;
      throw new ApiError(status, error, payload);
    }
    return payload as T;
  } catch (err) {
    if (!error) error = err instanceof Error ? err.message : String(err);
    throw err;
  } finally {
    const { tiers, trace, costUsd } = harvest(payload);
    logCall({
      method,
      path: url.pathname + (url.search || ''),
      status,
      ms: Math.round(performance.now() - startedAt),
      tiers,
      trace,
      costUsd,
      speculative: options.speculative ?? false,
      note: options.note,
      error,
    });
  }
}

function describe(payload: unknown): string {
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    const parts = [obj.error, obj.message, obj.reason].filter((p) => typeof p === 'string');
    if (parts.length) return parts.join(' — ');
  }
  return '';
}
